import { ProductFeatureConfig } from '../models/product-feature-config.interface';
import { ProductFeatureData } from '../models/product-feature-data.interface';
import type { HasFeatureParams } from '../models/feature-match-mode';
export type { HasFeatureParams } from '../models/feature-match-mode';

/**
 * Service that fetches and returns product features from the Product Feature API.
 */
export interface ProductFeatureService {
  /**
   * Initializes the service by fetching features from the API.
   *
   * @param config Configuration for the service (basePath, accountId, companiesBasePath)
   * @returns Promise that resolves when the service is initialized
   */
  initialize(config: ProductFeatureConfig): Promise<void>;

  /**
   * Returns the product feature payload as received from the API, plus the
   * service-derived `accountWorld` field.
   *
   * Prefer `hasFeature()` over reading the payload directly — it applies the correct resolution
   * (see below). `accountWorld` is metadata derived from the account's `contractType`.
   *
   * @returns The cached feature data, or null if not yet fetched
   */
  getFeatures(): Promise<ProductFeatureData | null>;

  /**
   * Checks whether the account has the requested feature(s). Each feature is resolved from
   * `channelLevel` first, then falls back to `accountLevel`; `accountWorld` does NOT affect the
   * result.
   *
   * - With `channelId`: that channel's feature set; without it: the channels are combined per
   *   `channelMatch` — `ChannelMatchMode.All` (default) requires every channel (intersection),
   *   `ChannelMatchMode.Some` accepts any channel (union).
   * - If the feature is not found in `channelLevel`, it is looked up in `accountLevel`.
   *
   * Each name in a multi-name check resolves independently before being combined by `mode`.
   * `mode` (feature names) and `channelMatch` (channels) are independent axes.
   */
  hasFeature(params: HasFeatureParams): Promise<boolean>;
}
